import { z } from 'zod';
import { Ulid } from './common.js';
import { DeviceIdentity } from './events.js';
/**
 * The store (`docs/store-plan.md`).
 *
 * One idea, written down once: **the `public` channel means "everyone"**. A build published
 * there is installable by any signed-in BoardFlight account — no redeem code, no grant, no team
 * membership — and everything in this module falls out of that. Browse, search and the store
 * page are the shapes a catalog needs to be honest about what is on it; the view report is what
 * a page sends back so a funnel has a first stage, and "popular" stands on plays the Worker
 * writes from each game's own session start.
 *
 * Nothing here is entitlement-gated. Every field on a `StoreCard` is either declared by the
 * developer (the listing rule: a screenshot is a file somebody uploaded, "1-4 players" is a
 * claim) or counted by BoardFlight, and the counts are rounded on the way out because an exact
 * number belongs to the developer's own dashboard and nowhere else.
 */
/* ------------------------------------------------------------------------ reserved slugs */
/**
 * Channel slugs with meaning attached to the word itself.
 *
 * `public` is the audience on the other side of a playtest — every app has one from creation
 * (migration 0020), and publishing to the store is what makes it *reachable* rather than what
 * makes it exist. `playground` is where the deploy button lands, twenty times an hour, which is
 * why it must never be a channel testers follow.
 *
 * The list is what "reserved" *means*, not what is refused: `playground` is created through the
 * ordinary channel path today and has to stay that way, so only `public` is refused below. What
 * they share is that a client naming one of these is naming a rule, not a channel it invented.
 */
export const RESERVED_CHANNEL_SLUGS = ['public', 'playground'];
/** The store's channel, by name, so nothing has to spell the string twice. */
export const PUBLIC_CHANNEL_SLUG = 'public';
/** The playground's, moved here from `apps/web/src/playground.ts` so both live in one list. */
export const PLAYGROUND_CHANNEL_SLUG = 'playground';
/* -------------------------------------------------------------------------- the catalog */
/**
 * Where a detail screen was opened from. The funnel cares; the ranking does not (§3.6).
 *
 * The first three are the Board's; `web` is a store page opened in a browser from a share link
 * (§14), sent by the page itself once it has rendered — never by the Worker that writes its
 * `<head>`, which is what a link-preview crawler reads.
 */
export const StoreViewContext = z.enum(['store', 'search', 'library', 'web']);
/**
 * One tile. Everything the storefront and search need, and nothing else.
 *
 * Deliberately not a `LibraryApp` in miniature: a card is read by the hundred on a shelf, and
 * the media list, the channel and the build a detail page needs are exactly what a browsing
 * player has not asked for yet. `GET /v1/store/apps/:appId` is where the rest lives.
 */
export const StoreCard = z.object({
    id: Ulid,
    name: z.string(),
    /**
     * What the game installs as on a Board.
     *
     * Here because the client cannot join a shelf to the device without it: "is this already on
     * this Board?" is answered by matching Board's own app list on the package id, and a tile that
     * could not answer it would offer Get for a game already on the Board the player is sitting at.
     * It is not a secret — the store page shows it, and so does every APK.
     */
    packageName: z.string(),
    /** The name, not the slug — the same word every screen shows beside a game. */
    teamName: z.string(),
    tagline: z.string().nullable(),
    /** In the developer's order, first one leading, exactly as `AppListing.genres` is. */
    genres: z.array(z.string()),
    playersMin: z.number().int().nullable(),
    playersMax: z.number().int().nullable(),
    iconUrl: z.string().nullable(),
    /**
     * The card's own picture, and the hero — each as uploaded, null where there is none. The
     * fallback between them is the renderer's, not the server's (tile → hero → icon on a card,
     * hero → tile → icon behind a page; `AppMediaKind` says why they differ), so a client can tell
     * a game that has a tile from one whose card is borrowing its hero.
     */
    tileUrl: z.string().nullable(),
    heroUrl: z.string().nullable(),
    /** The newest build visible on `public`, or null for an app that has none yet. */
    latestVersionName: z.string().nullable(),
    /**
     * Rounded (§5's display rule), and rounded *by the server* so a client cannot accidentally
     * publish a precision it was never given. "120+ plays" is what a player sees; the exact
     * number exists in the developer's own dashboard and nowhere a stranger can read it.
     */
    plays: z.number().int(),
    installs: z.number().int(),
    /**
     * The game's short link code (§14), so a card on the web can link to the game's own page:
     * `shareUrl(origin, shareCode)`. Every game a card is drawn for has one — it was given at the
     * first publish or by 0056 — and null is only the honest type of a column that is null for games
     * that were never near the store. The Board ignores it.
     */
    shareCode: z.string().nullable(),
    /**
     * The Board Piece Models a Board may install this game for, highest priority first — what the
     * `public` channel offers (`channelOffering`), the same menu its page's `supportedModels` is.
     *
     * Here so a card can say a game will not play with the pieces on somebody's table, which is a
     * fact about the game and their Board together and so is judged by the client
     * (`offerMissing`): no route knows a Board's pieces. With `modelChoice`, it is all that
     * judgment needs, and nothing a card adds is more than the game's page already shows anybody.
     * Optional on the wire, like `LibraryChannel.supportedModels`: absent is no judgment.
     */
    pieceModels: z.array(z.string()).optional(),
    /** The `public` channel's `ModelChoice`, as a string a newer server may extend — `LibraryChannel.modelChoice`. */
    modelChoice: z.string().optional(),
});
/**
 * A row of the storefront.
 *
 * `key` is a string rather than an enum because the genre shelves are composed from whatever
 * the visible games declare, and a client that has never heard of a genre must render the word
 * it was handed rather than drop the shelf — the same tolerance `AppListing.genres` has.
 */
export const StoreShelf = z.object({
    /** `popular`, `new`, or `genre:<word>`. */
    key: z.string(),
    title: z.string(),
    apps: z.array(StoreCard),
});
/**
 * Whether the hardware validation runner stands between a build and the store (§3.4).
 *
 * `enforced` is the rule: nothing unverified ships to strangers. `none` is what a deployment
 * with no runner configured answers, because the alternative is a permanently empty store —
 * and it is on the wire rather than assumed so that no client ever claims "verified on Board
 * hardware" about bytes nobody put on a Board.
 */
export const StoreVerificationMode = z.enum(['enforced', 'none']);
export const Storefront = z.object({
    generatedAt: z.number().int(),
    verification: StoreVerificationMode,
    shelves: z.array(StoreShelf),
});
/**
 * `GET /v1/store/search` — the query string, parsed.
 *
 * `genre` is singular: it is one filter chip, and it matches any game that declared that word
 * among its three. A game on three shelves is the point of the shelves, not a leak.
 */
export const StoreSearchQuery = z.object({
    q: z.string().trim().max(100).optional(),
    genre: z.string().trim().max(40).optional(),
    sort: z.enum(['popular', 'new', 'name']).default('popular'),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(24),
});
export const StoreSearchPage = z.object({
    items: z.array(StoreCard),
    nextCursor: z.string().nullable(),
});
/* --------------------------------------------------------------------------- telemetry */
/*
 * One write endpoint, auth-optional, answering 204 on well-formed input whatever happens next
 * (§3.6). It is analytics and not a receipt: the page fires and forgets, and a view naming an app
 * the caller cannot see is accepted and dropped rather than refused — a different answer would
 * make the endpoint an existence oracle for private games.
 *
 * There were two. A play is no longer reported by anybody: the Worker writes it from the game's
 * own session start (`docs/off-board-plan.md` §6), and `POST /v1/store/events/play` went with the
 * Android client that called it.
 *
 * `device` is how that client said which Board a view came from, and `nullish` because it
 * serialized with `encodeDefaults = true` — an explicit null on the wire, which `.optional()`
 * refuses. The storefront sends none.
 */
export const ViewEventRequest = z.object({
    appId: Ulid,
    context: StoreViewContext,
    device: DeviceIdentity.nullish(),
});
/**
 * How long the server treats repeats as one event.
 *
 * A reporter may keep its own marks as a courtesy (it stops obviously duplicate traffic), but
 * these are the rule: a crash loop or a mashed button is one session, not forty, and a detail page
 * re-opened while scrolling a shelf costs nothing.
 */
export const PLAY_EVENT_DEDUPE_MINUTES = 30;
export const VIEW_EVENT_DEDUPE_MINUTES = 10;
/** How long raw event rows live before the cron sweeps them; the aggregates are the record. */
export const STORE_EVENT_RETENTION_DAYS = 90;
/* --------------------------------------------------------------------------- readiness */
/**
 * What still blocks publishing, in the order a developer would fix them.
 *
 * A store page with no picture and no words is not a store page. The rule lives here so the
 * dashboard can show the checklist before the button is pressed and the server can re-check it
 * when it is — same list, one definition, no drift between the two.
 *
 * Not on the list, deliberately: the name, which an app always has; a hero image, which the
 * card falls back to the icon for; and anything about builds, because "is there something
 * installable here?" is a question the catalog asks every time it runs rather than once at
 * publish (§3.5) — an app can be published and not yet browsable, and that is an honest state.
 */
export const STORE_READINESS_ITEMS = [
    'tagline',
    'description',
    'genre',
    'icon',
    'screenshot',
];
export function storeReadiness(app) {
    const missing = [];
    // Trimmed, because a listing field holding a space is the same nothing an empty one is and
    // the dashboard's own inputs are the shortest path to one.
    if (!app.tagline?.trim())
        missing.push('tagline');
    if (!app.description?.trim())
        missing.push('description');
    if (app.genres.length === 0)
        missing.push('genre');
    if (!app.hasIcon)
        missing.push('icon');
    if (app.screenshotCount < 1)
        missing.push('screenshot');
    return missing;
}
/** One line per missing item, for whichever client is showing the checklist. */
export const STORE_READINESS_LABELS = {
    tagline: 'A tagline: one line under the name.',
    description: 'A description.',
    genre: 'At least one genre, so the game has a shelf.',
    icon: 'An icon.',
    screenshot: 'At least one screenshot.',
};
/* ------------------------------------------------------------------------- the review */
/**
 * What an operator decided about a submission (`docs/store-review-plan.md`). `withdrawn` is the
 * team's own, when it takes the game back off the store while the review waits. Mirrored by the
 * CHECK on `store_reviews.decision` in migration 0050.
 */
export const StoreReviewDecision = z.enum(['approved', 'rejected', 'withdrawn']);
/**
 * A game's latest submission, as its own team sees it.
 *
 * **Who decided is not on it.** An operator is BoardFlight to a team, not a person they share
 * anything with, and the decision is BoardFlight's. The note is — it is the operator's words *to*
 * the team, written for them to read.
 */
export const StoreReview = z.object({
    submittedAt: z.number().int(),
    /** Null while it waits. */
    decision: StoreReviewDecision.nullable(),
    decidedAt: z.number().int().nullable(),
    /** Required on a rejection, optional on an approval, absent on a withdrawal. */
    note: z.string().nullable(),
});
export function storeStanding(store) {
    // `approvedAt` absent is an API from before review existed, where publishing was enough — so a
    // published game reads as live there, which is what it was.
    if (store.approvedAt === undefined || store.approvedAt !== null) {
        return store.publishedAt !== null ? 'live' : 'off';
    }
    if (store.review?.decision === null)
        return 'in_review';
    if (store.review?.decision === 'rejected')
        return 'changes_requested';
    return 'draft';
}
/* ------------------------------------------------------------------------ share links */
/**
 * A store game's short link, `boardflight.app/g/<code>` (`docs/store-plan.md` §14).
 *
 * Six characters of lower-case Crockford base32: no `i`, `l`, `o` or `u`, so a code read aloud or
 * copied off a slide cannot be mistyped into another one. Thirty-two to the sixth is a billion
 * codes, so a collision is a retry rather than a design problem.
 *
 * A code, not the slug and not the id. Slugs are renamed (`UpdateAppRequest.slug`) and a link
 * posted somewhere cannot be edited afterwards; a ULID is 26 characters of noise in a tweet. A
 * game is given one code the first time it is published or submitted and keeps it for good —
 * unpublishing leaves it, so a link shared while the game was live works again the moment it is
 * back. It resolves only while the game is on the store, through the same predicate the catalog
 * uses, so a code is never an oracle for a game nobody may see.
 */
export const SHARE_CODE_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';
export const SHARE_CODE_LENGTH = 6;
/** The web path a code lives under. The web Worker answers it first, so a crawler gets the
 *  game's own `<head>` (`apps/web/worker/index.ts`). */
export const SHARE_PATH = '/g';
/** Upper case and stray whitespace are accepted and normalized, since somebody will type one. */
export const ShareCode = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[0-9a-hjkmnp-tv-z]{6}$/, 'Not a share code.');
/** A fresh code. 256 is a multiple of 32, so masking each byte keeps every character equally likely. */
export function newShareCode() {
    const bytes = crypto.getRandomValues(new Uint8Array(SHARE_CODE_LENGTH));
    return Array.from(bytes, (byte) => SHARE_CODE_ALPHABET[byte & 31]).join('');
}
/** The link itself, on whichever origin serves the dashboard. */
export function shareUrl(origin, code) {
    return `${origin.replace(/\/+$/, '')}${SHARE_PATH}/${code}`;
}
/* -------------------------------------------------------------------- the store page */
/*
 * `GET /v1/store/apps/:appId`'s response is `StoreAppDetail`, and it lives in `library.ts`
 * beside the `LibraryApp` it is mostly made of — this module is imported by `apps.ts` and
 * importing the library back from here would close a cycle that TDZs at load time.
 */
/** What a "Get" answers with; the library refresh that follows is what actually entitles. */
export const StoreLibraryResponse = z.object({
    /** Whether this account has the game in its library, after the call. */
    added: z.boolean(),
});
//# sourceMappingURL=store.js.map